import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Tag,
  Typography,
  Upload
} from "antd";
import { DeleteOutlined, EditOutlined, PaperClipOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import PageToolbar from "../../../design-system/PageToolbar";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import SignatureField from "./SignatureField";
import {
  createQualification,
  deleteQualification,
  listQualifications,
  qualificationFileUrl,
  updateQualification,
  uploadQualificationFile
} from "./api";
import {
  LIGHT_COLORS,
  formatDay,
  qualificationLabels,
  stateLabels,
  vorsorgeLabels
} from "./format";

const toDay = value => (value ? value.format("YYYY-MM-DD") : "");

/** Add or edit one qualification, Vorsorge or Führerscheinkontrolle (with the checker's signature). */
export const QualificationDialog = ({ open, record, meta, employeeId, onClose, onSaved }) => {
  const { t } = useTranslation("dashboard");
  const [form] = Form.useForm();
  const pad = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const type = Form.useWatch("type", form);
  const types = qualificationLabels(t);
  const kinds = vorsorgeLabels(t);
  const typeInfo = (meta?.qualificationTypes || []).find(q => q.key === type) || {};

  useEffect(() => {
    if (!open) return;
    setError("");
    form.resetFields();
    form.setFieldsValue(
      record
        ? {
            ...record,
            issuedOn: record.issuedOn ? dayjs(record.issuedOn) : null,
            validUntil: record.validUntil ? dayjs(record.validUntil) : null,
            classes: record.licenseCheck?.classes || "",
            checkerName: record.licenseCheck?.checkerName || ""
          }
        : { employee: employeeId, type: "ERSTHELFER", issuedOn: dayjs(), vorsorgeKind: "" }
    );
  }, [open]);

  const submit = async values => {
    setError("");
    const body = {
      type: values.type,
      title: values.title || "",
      vorsorgeKind: values.type === "VORSORGE" ? values.vorsorgeKind : "",
      issuedOn: toDay(values.issuedOn),
      validUntil: toDay(values.validUntil),
      issuer: values.issuer || "",
      certificateNumber: values.certificateNumber || "",
      note: values.note || ""
    };
    if (typeInfo.isCheck) {
      const signed = pad.current && !pad.current.isEmpty();
      if (!signed && !record?.licenseCheck) return setError(t("Please sign in the box."));
      if (signed || !record) {
        body.licenseCheck = {
          classes: values.classes || "",
          checkerName: values.checkerName || "",
          signature: signed ? pad.current.toDataURL() : ""
        };
      }
    }
    setBusy(true);
    try {
      const res = record
        ? await updateQualification(record._id, body)
        : await createQualification({ ...body, employee: values.employee });
      Success(t("Saved"));
      onSaved(res.data.data);
    } catch (err) {
      generalErrorAlert(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      rootClassName="safety-page"
      open={open}
      width={640}
      title={record ? t("Edit qualification") : t("Add qualification or check")}
      okText={t("Save")}
      cancelText={t("Cancel")}
      confirmLoading={busy}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnClose
      maskClosable={false}
    >
      <Form form={form} layout="vertical" onFinish={submit}>
        <Row gutter={[12, 0]}>
          <Col xs={24} md={12}>
            <Form.Item name="employee" label={t("Employee")} rules={[{ required: true }]}>
              <Select
                showSearch
                disabled={Boolean(record) || Boolean(employeeId)}
                optionFilterProp="label"
                options={(meta?.employees || []).map(e => ({ value: e._id, label: e.name }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="type" label={t("Type")} rules={[{ required: true }]}>
              <Select
                options={(meta?.qualificationTypes || []).map(q => ({
                  value: q.key,
                  label: types[q.key]
                }))}
              />
            </Form.Item>
          </Col>
          {type === "VORSORGE" && (
            <Col xs={24} md={12}>
              <Form.Item name="vorsorgeKind" label={t("Kind")} rules={[{ required: true }]}>
                <Select
                  options={(meta?.vorsorgeKinds || []).map(k => ({ value: k, label: kinds[k] }))}
                />
              </Form.Item>
            </Col>
          )}
          <Col xs={24} md={type === "VORSORGE" ? 12 : 24}>
            <Form.Item
              name="title"
              label={
                type === "VORSORGE" ? t("Reason (e.g. noise, respiratory protection)") : t("Name")
              }
              rules={[{ required: type === "VORSORGE" || type === "OTHER" }]}
            >
              <Input maxLength={200} />
            </Form.Item>
          </Col>
          <Col xs={12}>
            <Form.Item
              name="issuedOn"
              label={typeInfo.isCheck ? t("Checked on") : t("Date")}
              rules={[{ required: true }]}
            >
              <DatePicker className="sf-full" format="DD.MM.YYYY" allowClear={false} />
            </Form.Item>
          </Col>
          <Col xs={12}>
            <Form.Item
              name="validUntil"
              label={typeInfo.needsNextDate ? t("Next check-up") : t("Valid until")}
              rules={[{ required: Boolean(typeInfo.needsNextDate) }]}
              extra={
                typeInfo.months && !typeInfo.needsNextDate
                  ? t("Empty: {{n}} months after the date", { n: typeInfo.months })
                  : null
              }
            >
              <DatePicker className="sf-full" format="DD.MM.YYYY" />
            </Form.Item>
          </Col>
          {!typeInfo.isCheck && (
            <>
              <Col xs={24} md={12}>
                <Form.Item name="issuer" label={t("Issued by")}>
                  <Input maxLength={200} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="certificateNumber" label={t("Certificate number")}>
                  <Input maxLength={100} />
                </Form.Item>
              </Col>
            </>
          )}
          {typeInfo.isCheck && (
            <>
              <Col xs={24} md={12}>
                <Form.Item name="classes" label={t("Licence classes seen")}>
                  <Input maxLength={100} placeholder="B, BE, C1" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="checkerName" label={t("Checked by")}>
                  <Input maxLength={120} />
                </Form.Item>
              </Col>
            </>
          )}
        </Row>
        <Form.Item name="note" label={t("Note")}>
          <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} maxLength={2000} />
        </Form.Item>
        {typeInfo.isCheck && (
          <SignatureField
            ref={pad}
            label={
              record?.licenseCheck
                ? t("Sign again only to replace the signature")
                : t("Signature of the person checking the licence")
            }
          />
        )}
        {error && <Alert type="error" showIcon message={error} />}
      </Form>
    </Modal>
  );
};

/** Files of a record: links plus an upload button (certificate, photo of the licence). */
export const QualificationFiles = ({ record, canEdit, onChanged }) => {
  const { t } = useTranslation("dashboard");
  const upload = async ({ file, onSuccess, onError }) => {
    try {
      const res = await uploadQualificationFile(record._id, file);
      onChanged(res.data.data);
      onSuccess();
    } catch (error) {
      generalErrorAlert(error);
      onError(error);
    }
  };
  return (
    <Space size={[4, 4]} wrap>
      {(record.attachments || []).map(a => (
        <a
          key={a._id}
          href={qualificationFileUrl(record._id, a._id)}
          target="_blank"
          rel="noreferrer"
        >
          <PaperClipOutlined /> {a.name}
        </a>
      ))}
      {canEdit && (
        <Upload customRequest={upload} showUploadList={false} accept=".pdf,.jpg,.jpeg,.png,.webp">
          <Button size="small" icon={<PaperClipOutlined />}>
            {t("Add file")}
          </Button>
        </Upload>
      )}
    </Space>
  );
};

/** All qualification records, current ones highlighted, with filters. */
const Qualifications = ({ meta }) => {
  const { t } = useTranslation("dashboard");
  const [employee, setEmployee] = useState();
  const [type, setType] = useState();
  const [currentOnly, setCurrentOnly] = useState(true);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const labels = stateLabels(t);
  const types = qualificationLabels(t);

  const load = async () => {
    setLoading(true);
    try {
      const res = await listQualifications({ employee, type });
      setData(res.data.data);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [employee, type]);

  const remove = async id => {
    try {
      await deleteQualification(id);
      load();
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  const rows = currentOnly ? data.filter(r => r.current) : data;

  return (
    <>
      <PageToolbar
        filters={
          <Space wrap>
            <Select
              allowClear
              showSearch
              className="sf-filter"
              placeholder={t("Employee")}
              optionFilterProp="label"
              value={employee}
              onChange={setEmployee}
              options={(meta?.employees || []).map(e => ({ value: e._id, label: e.name }))}
            />
            <Select
              allowClear
              className="sf-filter"
              placeholder={t("Type")}
              value={type}
              onChange={setType}
              options={Object.entries(types).map(([value, label]) => ({ value, label }))}
            />
            <Space>
              <Switch checked={currentOnly} onChange={setCurrentOnly} />
              <span>{t("Current only")}</span>
            </Space>
          </Space>
        }
        actions={
          meta?.can.create && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditing({})}>
              {t("Add qualification or check")}
            </Button>
          )
        }
      />
      <ResponsiveTable
        rowKey="_id"
        loading={loading}
        dataSource={rows}
        mobileAvatar={false}
        locale={{ emptyText: t("No qualifications recorded") }}
        pagination={rows.length > 50 ? { pageSize: 50 } : false}
        columns={[
          { title: t("Employee"), dataIndex: "employeeName", key: "employee", mobile: "title" },
          {
            title: t("Qualification"),
            key: "type",
            mobile: "subtitle",
            render: (_, row) => (
              <span className="sf-wrap">
                {[types[row.type], row.title].filter(Boolean).join(": ")}
                {row.type === "FUEHRERSCHEIN" && row.licenseCheck?.classes
                  ? ` (${row.licenseCheck.classes})`
                  : ""}
              </span>
            )
          },
          {
            title: t("Status"),
            key: "state",
            mobile: "badge",
            render: (_, row) =>
              row.current ? (
                <Tag color={LIGHT_COLORS[row.light]}>{labels[row.state]}</Tag>
              ) : (
                <Tag>{t("Replaced")}</Tag>
              )
          },
          { title: t("Date"), key: "issued", render: (_, row) => formatDay(row.issuedOn) },
          {
            title: t("Valid until"),
            key: "valid",
            render: (_, row) => formatDay(row.validUntil) || "—"
          },
          {
            title: t("Files"),
            key: "files",
            mobile: "full",
            render: (_, row) => (
              <QualificationFiles record={row} canEdit={meta?.can.update} onChanged={load} />
            )
          },
          {
            title: "",
            key: "actions",
            mobile: "actions",
            render: (_, row) => (
              <Space>
                {meta?.can.update && (
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    aria-label={t("Edit")}
                    onClick={() => setEditing(row)}
                  />
                )}
                {meta?.can.delete && (
                  <Popconfirm title={t("Delete this record?")} onConfirm={() => remove(row._id)}>
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      aria-label={t("Delete")}
                    />
                  </Popconfirm>
                )}
              </Space>
            )
          }
        ]}
      />
      <Typography.Paragraph type="secondary" className="sf-small mt-3">
        {t(
          "A new record of the same kind replaces the earlier one. Expiring records are reminded 30 days ahead and when they lapse."
        )}
      </Typography.Paragraph>
      <QualificationDialog
        open={Boolean(editing)}
        record={editing?._id ? editing : null}
        meta={meta}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          load();
        }}
      />
    </>
  );
};

export default Qualifications;
